import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Pencil, ArrowLeft, Mail, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const CustomerManagement = () => {
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);
  const [customers, setCustomers] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    type: 'commercial',
    industry: 'office',
    status: 'active',
    main_contact_name: '',
    main_contact_email: '',
    main_contact_phone: '',
    notes: '',
    cc_email_list: []
  });

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(response.data);
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Failed to fetch customers');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Parse cc_email_list if it's a comma-separated string
      const submitData = {
        ...formData,
        cc_email_list: typeof formData.cc_email_list === 'string' 
          ? formData.cc_email_list.split(',').map(e => e.trim()).filter(e => e)
          : formData.cc_email_list
      };

      if (editingCustomer) {
        await axios.put(`${API}/customers/${editingCustomer.id}`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Customer updated successfully!');
      } else {
        await axios.post(`${API}/customers`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Customer created successfully!');
      }
      fetchCustomers();
      setIsDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error('Submit error:', error);
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleEmailClick = (customer) => {
    if (!customer.main_contact_email) {
      toast.error('No email is set for this customer');
      return;
    }
    
    const subject = encodeURIComponent(`Service Request - ${customer.name}`);
    const mailtoLink = `mailto:${customer.main_contact_email}?subject=${subject}`;
    window.location.href = mailtoLink;
  };

  const openEditDialog = (customer) => {
    setEditingCustomer(customer);
    setFormData({
      name: customer.name || '',
      type: customer.type || 'commercial',
      industry: customer.industry || 'office',
      status: customer.status || 'active',
      main_contact_name: customer.main_contact_name || '',
      main_contact_email: customer.main_contact_email || '',
      main_contact_phone: customer.main_contact_phone || '',
      notes: customer.notes || '',
      cc_email_list: Array.isArray(customer.cc_email_list) 
        ? customer.cc_email_list.join(', ') 
        : ''
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      type: 'commercial',
      industry: 'office',
      status: 'active',
      main_contact_name: '',
      main_contact_email: '',
      main_contact_phone: '',
      notes: '',
      cc_email_list: []
    });
    setEditingCustomer(null);
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
          <h1 className="text-2xl font-bold">Customer Master Table</h1>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>All Customers ({customers.length})</CardTitle>
            <div className="flex gap-2">
              <Button onClick={fetchCustomers} variant="outline">
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
              <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
                <DialogTrigger asChild>
                  <Button style={{ backgroundColor: '#6B9F3E' }}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Customer
                  </Button>
                </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingCustomer ? 'Edit Customer' : 'Add New Customer'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Customer Name */}
                  <div className="space-y-2">
                    <Label htmlFor="name">Customer Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                    />
                  </div>

                  {/* Type */}
                  <div className="space-y-2">
                    <Label htmlFor="type">Type *</Label>
                    <Select
                      value={formData.type}
                      onValueChange={(value) => setFormData({ ...formData, type: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="commercial">Commercial</SelectItem>
                        <SelectItem value="financial">Financial</SelectItem>
                        <SelectItem value="education">Education</SelectItem>
                        <SelectItem value="retail">Retail</SelectItem>
                        <SelectItem value="healthcare">Healthcare</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Industry */}
                  <div className="space-y-2">
                    <Label htmlFor="industry">Industry *</Label>
                    <Select
                      value={formData.industry}
                      onValueChange={(value) => setFormData({ ...formData, industry: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select industry" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="office">Office</SelectItem>
                        <SelectItem value="banking">Banking</SelectItem>
                        <SelectItem value="education">Education</SelectItem>
                        <SelectItem value="retail">Retail</SelectItem>
                        <SelectItem value="healthcare">Healthcare</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Status */}
                  <div className="space-y-2">
                    <Label htmlFor="status">Status *</Label>
                    <Select
                      value={formData.status}
                      onValueChange={(value) => setFormData({ ...formData, status: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="border-t pt-4 mt-4">
                    <h3 className="font-semibold mb-3">Main Contact Information</h3>
                    
                    {/* Main Contact Name */}
                    <div className="space-y-2">
                      <Label htmlFor="main_contact_name">Main Contact Name</Label>
                      <Input
                        id="main_contact_name"
                        value={formData.main_contact_name}
                        onChange={(e) => setFormData({ ...formData, main_contact_name: e.target.value })}
                        placeholder="e.g., John Smith"
                      />
                    </div>

                    {/* Main Contact Email */}
                    <div className="space-y-2 mt-3">
                      <Label htmlFor="main_contact_email">Main Contact Email</Label>
                      <Input
                        id="main_contact_email"
                        type="email"
                        value={formData.main_contact_email}
                        onChange={(e) => setFormData({ ...formData, main_contact_email: e.target.value })}
                        placeholder="e.g., john.smith@company.com"
                      />
                    </div>

                    {/* Main Contact Phone */}
                    <div className="space-y-2 mt-3">
                      <Label htmlFor="main_contact_phone">Main Contact Phone</Label>
                      <Input
                        id="main_contact_phone"
                        type="tel"
                        value={formData.main_contact_phone}
                        onChange={(e) => setFormData({ ...formData, main_contact_phone: e.target.value })}
                        placeholder="e.g., (239) 821-5640"
                      />
                    </div>

                    {/* CC Email List */}
                    <div className="space-y-2 mt-3">
                      <Label htmlFor="cc_email_list">CC Email List</Label>
                      <Input
                        id="cc_email_list"
                        value={formData.cc_email_list}
                        onChange={(e) => setFormData({ ...formData, cc_email_list: e.target.value })}
                        placeholder="email1@example.com, email2@example.com"
                      />
                      <p className="text-xs text-gray-500">Comma-separated list of emails to CC on correspondence</p>
                    </div>

                    {/* Notes */}
                    <div className="space-y-2 mt-3">
                      <Label htmlFor="notes">Notes</Label>
                      <Textarea
                        id="notes"
                        value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        placeholder="Service locations, special instructions, etc."
                        rows={3}
                      />
                    </div>
                  </div>

                  <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => { setIsDialogOpen(false); resetForm(); }}>
                      Cancel
                    </Button>
                    <Button type="submit" style={{ backgroundColor: '#6B9F3E' }}>
                      {editingCustomer ? 'Update Customer' : 'Create Customer'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {customers.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No customers yet. Add your first customer to get started.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left py-3 px-4 font-semibold">Customer Name</th>
                    <th className="text-left py-3 px-4 font-semibold">Main Contact</th>
                    <th className="text-left py-3 px-4 font-semibold">Email</th>
                    <th className="text-right py-3 px-4 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {customers.map((customer, index) => (
                    <tr key={customer.id} className={`border-b hover:bg-gray-50 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                      <td className="py-3 px-4 font-medium">{customer.name}</td>
                      <td className="py-3 px-4">{customer.main_contact_name || '-'}</td>
                      <td className="py-3 px-4 text-sm">{customer.main_contact_email || '-'}</td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex justify-end gap-2">
                          {/* Email Icon */}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEmailClick(customer);
                            }}
                            title="Send email to main contact"
                            disabled={!customer.main_contact_email}
                            className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            <Mail className="h-4 w-4" />
                          </Button>
                          
                          {/* Edit Icon */}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              openEditDialog(customer);
                            }}
                            title="Edit customer"
                            className="text-amber-600 hover:text-amber-800 hover:bg-amber-50"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CustomerManagement;
